// Card "Próximos eventos" na home do aluno. Mostra até 5 próximas aulas
// agendadas (não-canceladas, não-passadas). Some se vazio.

import { useState } from "preact/hooks";
import { CalendarClock, ChevronRight } from "lucide-react";
import { AulaModal, upcomingAulas } from "./AgendaView";
import type { AulaStudentRow } from "./api";

interface Props {
  onOpenAll: () => void;
}

/** Bloco vertical de data/hora — peça mais importante visualmente do card. */
function DateBlock({ iso }: { iso: string }) {
  const t = new Date(iso);
  const now = new Date();
  const sameDay =
    t.getFullYear() === now.getFullYear() &&
    t.getMonth() === now.getMonth() &&
    t.getDate() === now.getDate();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow =
    t.getFullYear() === tomorrow.getFullYear() &&
    t.getMonth() === tomorrow.getMonth() &&
    t.getDate() === tomorrow.getDate();
  const time = t.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit"
  });

  let topLine: string;
  let bigLine: string;
  let highlight = false;
  if (sameDay) {
    topLine = "HOJE";
    bigLine = time;
    highlight = true;
  } else if (isTomorrow) {
    topLine = "AMANHÃ";
    bigLine = time;
    highlight = true;
  } else {
    const weekday = t
      .toLocaleDateString("pt-BR", { weekday: "short" })
      .replace(".", "")
      .toUpperCase();
    const day = t.getDate().toString().padStart(2, "0");
    topLine = weekday;
    bigLine = day;
  }

  return (
    <div
      class={`flex w-14 shrink-0 flex-col items-center justify-center rounded-md px-1.5 py-1.5 ring-1 ${
        highlight
          ? "bg-emerald-500/15 text-emerald-700 ring-emerald-500/30 dark:text-emerald-400"
          : "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20 dark:text-emerald-400"
      }`}
    >
      <span class="text-[9px] font-bold leading-none tracking-wider">
        {topLine}
      </span>
      <span class="mt-0.5 text-base font-extrabold leading-none tabular-nums">
        {bigLine}
      </span>
      {!highlight && (
        <span class="mt-0.5 text-[10px] font-semibold leading-none tabular-nums opacity-80">
          {time}
        </span>
      )}
    </div>
  );
}

export function ProximosEventosCard({ onOpenAll }: Props) {
  const list = upcomingAulas.value;
  const isEmpty = list.length === 0;
  const [openAula, setOpenAula] = useState<AulaStudentRow | null>(null);
  return (
    <section class="overflow-hidden rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3 sm:p-4">
      <header class="mb-3 flex items-center justify-between gap-2">
        <h2 class="flex min-w-0 items-center gap-2 text-sm font-bold text-emerald-700 dark:text-emerald-400">
          <CalendarClock className="size-4 shrink-0" />
          <span class="truncate">Próximos eventos</span>
          {list.length > 0 && (
            <span class="inline-flex h-5 min-w-[1.5rem] shrink-0 items-center justify-center rounded-full bg-emerald-500/15 px-1.5 text-[11px] font-bold tabular-nums text-emerald-700 ring-1 ring-emerald-500/25 dark:text-emerald-400">
              {list.length}
            </span>
          )}
        </h2>
        <button
          type="button"
          onClick={onOpenAll}
          class="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline dark:text-emerald-400"
        >
          Ver agenda <ChevronRight className="size-3.5" />
        </button>
      </header>
      {isEmpty ? (
        <p class="rounded-md border border-dashed border-emerald-500/20 bg-background/40 px-3 py-4 text-center text-[11px] text-muted-foreground">
          Sem aulas marcadas no momento.
        </p>
      ) : (
        <ul class="space-y-1.5">
          {list.map((a) => (
            <li
              key={a.id}
              onClick={() => setOpenAula(a)}
              class="flex cursor-pointer items-center gap-3 rounded-md border border-border bg-background/60 px-2 py-2 hover:bg-accent/50"
            >
              <DateBlock iso={a.dataHora} />
              <div class="min-w-0 flex-1">
                <div class="truncate text-xs font-bold">{a.disciplinaName}</div>
                <div class="mt-0.5 truncate text-[10px] text-muted-foreground">
                  com {a.professorName}
                  {a.descricao ? ` · ${a.descricao}` : ""}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {openAula && (
        <AulaModal a={openAula} onClose={() => setOpenAula(null)} />
      )}
    </section>
  );
}
